import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./components/Sidebar/Sidebar";
import ArticleFeed from "./components/ArticleFeed/ArticleFeed";
import FilterBar from "./components/FilterBar/FilterBar";
import Pagination from "./components/Pagination/Pagination";
import { Article } from "./types";
import { fetchArticles, searchArticles } from "./services/api";
import Header from "./components/Header/Header";
import "./App.css";

const ARTICLES_PER_PAGE = 10;

function App() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedSource, setSelectedSource] = useState<string>("All");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  // State for the filters
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [appliedFilters, setAppliedFilters] = useState<{
    from?: string;
    to?: string;
    query?: string;
  }>({});

  const getArticles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let response;
      const baseParams = {
        page: currentPage,
        limit: ARTICLES_PER_PAGE,
      };

      if (appliedFilters.query) {
        // If there's a search query, use the search endpoint
        response = await searchArticles({
          ...baseParams,
          query: appliedFilters.query,
        });
      } else {
        // Otherwise, use the standard fetch endpoint with filters
        response = await fetchArticles({
          ...baseParams,
          category: selectedCategory,
          source: selectedSource,
          from_date: appliedFilters.from,
          to_date: appliedFilters.to,
        });
      }

      setArticles(response.articles);
      setTotalPages(Math.ceil(response.total_articles / ARTICLES_PER_PAGE));
    } catch (err) {
      setError("Failed to load articles. Please try again later.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, selectedSource, currentPage, appliedFilters]);

  useEffect(() => {
    // When filters are applied, we want to fetch articles.
    // This effect will run when any of the dependencies change.
    getArticles();
  }, [getArticles]);

  const handleSelectCategory = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    setSearchQuery(""); // Clear search when changing category
    setAppliedFilters({ from: fromDate, to: toDate, query: "" });
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const handleSelectSource = (source: string) => {
    setSelectedSource(source);
    setCurrentPage(1);
    setSearchQuery(""); // Clear search when changing source
    setAppliedFilters({ from: fromDate, to: toDate, query: "" });
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    // If there's a search query, it overrides other filters
    if (searchQuery) {
      setSelectedCategory("All");
      setSelectedSource("All");
      setFromDate("");
      setToDate("");
    }
    setAppliedFilters({ from: fromDate, to: toDate, query: searchQuery });
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="App">
      <Header onMenuClick={toggleSidebar} />
      <Sidebar
        selectedCategory={selectedCategory}
        selectedSource={selectedSource}
        onSelectCategory={handleSelectCategory}
        onSelectSource={handleSelectSource}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <div className="main-content">
        <FilterBar
          fromDate={fromDate}
          toDate={toDate}
          searchQuery={searchQuery}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onSearchQueryChange={setSearchQuery}
          onApplyFilters={handleApplyFilters}
        />
        <ArticleFeed articles={articles} isLoading={isLoading} error={error} />
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}

export default App;
